<template>
    <p-feature-flag
    name="Accounting"
    redirect>
        <p-search-screen
                title="General Ledger Detail"
                api-destination="generalledgers"
                :filters="filters"
                :fields="fields"
                :filterBy="filterBy"
                use-bootstrap-selection
                ref="searchScreen"    
                add-new-button
                @total-rows-changed="e => (totalRows = e)"
            >
            <template v-slot:actionButtons>
                <b-row class="pl-2">                       
                    <b-col>
                        <b-button
                            variant="outline-primary"
                            v-on:click="printScreen()"
                            :disabled="totalRows <= 0"
                            class="me-2">
                            Print
                        </b-button>
                        <b-button 
                            variant="outline-primary" 
                            @click="downloadFile('csv')" 
                            :disabled="totalRows <= 0"
                            class="me-2">
                            Export (CSV)
                        </b-button>
                        <b-button
                            variant="outline-primary" 
                            :to="{ path: '/generalledgers' }"
                            class="me-2">
                            Back to General Ledger
                        </b-button>                        
                    </b-col>
                </b-row>
            </template>
            <template v-slot:belowSearch>
                <p-form>
                    <b-row class="mt-4">
                        <b-col cols="2" sm="4" class="me-2 ml-4">
                            <p-datepicker label="Start Activity Date"
                                v-model="dateRange.startDate"
                            vid="activityStartDate" />
                        </b-col>
                        <b-col cols="2" sm="4">
                            <p-datepicker label="End Activity Date"
                                vid="endDate"
                                rules="dateGreaterOrEqual:@activityStartDate"
                                v-model="dateRange.endDate" />
                        </b-col>
                    </b-row>
                </p-form>
            </template>
        </p-search-screen>
	</p-feature-flag>
</template>

<script>
import { datetimeToLocalTimeFormatter, currencyFormatter, dateOnlyFormatter } from '../../components/Common/Formatters.js';
import axios from 'axios';
import { downloadFileToBrowser } from '@/components/Common/BrowserDownload.js';
import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';
import SearchStorage from '@/components/mixins/SearchFilterStorage.js';
import DateOnlyPickerFilters from '@/components/mixins/DateOnlyPickerFilters.js';

export default {
    // DateOnlyPickerFilters must follow SearchStorage — it seeds from the filters that mixin restores.
    mixins: [SearchStorage, DateOnlyPickerFilters],
    props: {
        customers: Object,
        account: Object
    },
    methods: {
        printScreen(){
            window.print();
        },
        dccFormatter(value) {
            return value.padStart(6, '0'); // Fixes DCCs like 030101 which would otherwise be displayed as 30101
        },
        async downloadFile(exportFormat) {
            let apiUrl = 'GeneralLedgers/csv';
            this.isBusy = true;
            axios.post(
                    apiUrl,
                    this.filters,
                    { responseType: 'blob' })
                .then(result => {
                    downloadFileToBrowser(
                        result.data,
                        exportFormat == 'pdf' ? `General Ledger` : `General Ledger.csv`,
                        exportFormat == 'pdf' ? 'application/pdf' : 'application/csv');

                        this.$appSuccess(`General Ledger report generated successfully.`)
                })
                .finally(() => {
                    this.isBusy = false;
            });
        }
    },
    data() {
        return {
            filterName: 'GeneralLedgerDetail',
            selectedCustomers: [],
            isBusy: false,
            totalRows:0,
            // Datepicker-bound models hold the store-tz ISO the picker emits; DateOnlyPickerFilters
            // translates between these and the YYYY-MM-DD filters below. Neither picker carries
            // is-end-date, so neither pairing sets it.
            dateRange: {
                startDate: null,
                endDate: null
            },
            datePickerFilters: [
                { model: 'dateRange.startDate', filter: 'startDate' },
                { model: 'dateRange.endDate', filter: 'endDate' }
            ],
            filters: {
                automatic: '',
                searchBy: 'All',
                perPage: 50,
                sortBy: 'createDate',
                sortDesc: true,
                startDate: null,                
                endDate: null,
                locationId: null,
            },
            filterBy: ['All','GL Code', 'Entry Type', 'Entry Reference', 'Amount'],
            fields: [            
            {
                    label: 'Location',
                    key: 'locationName',
                    sortable: true
                },
                {
                    key: 'accountingDate',
                    sortable: true
                },
                {
                    label: 'Activity Date',
                    key: 'activityDate',
                    formatter: dateOnlyFormatter,
                    sortable: true,
                    sortByformatted: true
                },                
                {
                    key: 'userName',
                    sortable: true
                },
                {
                    label: 'GL Code',
                    key: 'glCode',
                    sortable: true,
                },
                {
                    label: 'GL Code Description',
                    key: 'glCodeDescription',
                    sortable: true,
                },                
                {
                    key: 'entryType',
                    sortable: true,
                },
                {
                    key: 'entryReference',
                    sortable: true
                },
                {
                    formatter: currencyFormatter,
                    key: 'debit',
                    sortable: true,
                    tdClass: 'text-center',
                    sortByformatted: true
                },   
                {
                    formatter: currencyFormatter,
                    key: 'credit',
                    sortable: true,
                    tdClass: 'text-center'
                },             
                {
                    label: 'Entry Date',
                    key: 'createDate',
                    formatter: datetimeToLocalTimeFormatter,
                    sortable: true,
                    sortByformatted: true
                }
            ]
        };
    },
    mounted : async function() {
        this.filters.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();
    }
};
</script>
